import { useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  IconMapPin,
  IconBriefcase,
  IconMail,
  IconWorld,
  IconSchool,
  IconHeart,
  IconBulb,
  IconListCheck,
  IconMessageChatbot,
  IconChartBar,
  IconCalendar,
  IconPencil,
  IconShare3,
  IconDownload,
  IconBrandFacebook,
  IconBrandLinkedin,
  IconBrandX,
  IconBrandWhatsapp,
} from "@tabler/icons-react";
import classes from "./reviewCard.module.css";
import { currencyFormat, convertToBase64, shareProfile } from "../../../utils/functions";
import { uploadTalentProfilePhoto } from "../../../services/talentCalls";
import { mapToScoreComparisonData } from "../../ClientDashboard/TalentProfile/components/ScoreComparison";

const ratingFor = (score) => {
  if (score >= 85) return { label: "Excellent", cls: classes.excellent };
  if (score >= 70) return { label: "Good", cls: classes.good };
  return { label: "Fair", cls: classes.fair };
};

const ReviewCard = ({ data }) => {
  const navigate = useNavigate();
  const [avatarFile, setAvatarFile] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  const initials = `${data?.firstName?.[0] || ""}${data?.lastName?.[0] || ""}`.toUpperCase();
  const experiences = data?.workHistoryExperience || [];
  const earliestStart = experiences
    .map((e) => e?.startDate)
    .filter(Boolean)
    .sort((a, b) => new Date(a) - new Date(b))[0];
  const yearsExperience = earliestStart ? moment().diff(moment(earliestStart), "years") : null;
  const scores = mapToScoreComparisonData(data);

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !data?._id) return;
    setAvatarFile(file);
    try {
      const base64Image = await convertToBase64(file);
      await uploadTalentProfilePhoto(data._id, { avatar: base64Image });
    } catch (_) {}
  };

  return (
    <div className={classes.wrap}>
      <div className={classes.grid}>
        {/* Identity */}
        <div className={classes.side}>
          <div className={classes.avatarRow}>
            <label className={classes.avatar} htmlFor="stage7_avatar_upload">
              {avatarFile || data?.avatar ? (
                <img src={avatarFile ? URL.createObjectURL(avatarFile) : data.avatar} alt="" />
              ) : (
                initials
              )}
            </label>
            <input id="stage7_avatar_upload" type="file" accept="image/*" style={{ display: "none" }} onChange={handleAvatarChange} />

            <div className={classes.sideActions} style={{ position: "relative" }}>
              <button type="button" className={classes.iconBtn} onClick={() => setShareOpen((p) => !p)} aria-label="Share profile">
                <IconShare3 size={16} />
              </button>
              <button type="button" className={classes.iconBtn} onClick={() => navigate("/talent-onboarding?stage=2")} aria-label="Edit profile">
                <IconPencil size={16} />
              </button>
              {shareOpen && (
                <div className={classes.shareMenu}>
                  <IconBrandFacebook onClick={() => shareProfile("facebook")} color="#1877F2" />
                  <IconBrandLinkedin onClick={() => shareProfile("linkedin")} color="#0A66C2" />
                  <IconBrandX onClick={() => shareProfile("twitter")} color="#0d1b3e" />
                  <IconBrandWhatsapp onClick={() => shareProfile("whatsapp")} color="#25D366" />
                </div>
              )}
            </div>
          </div>

          <div className={classes.name}>
            {data?.firstName} {data?.lastName}
          </div>
          <div className={classes.role}>{data?.pryRole}</div>

          <div className={classes.metaList}>
            {data?.currentLocation && (
              <div className={classes.metaItem}>
                <IconMapPin size={15} /> {data.currentLocation}
              </div>
            )}
            {yearsExperience !== null && (
              <div className={classes.metaItem}>
                <IconBriefcase size={15} /> {yearsExperience} years experience
              </div>
            )}
            {data?.email && (
              <div className={classes.metaItem}>
                <IconMail size={15} /> {data.email}
              </div>
            )}
          </div>

          <div className={classes.salary}>
            <div className={classes.salaryLabel}>Salary Expectation</div>
            <div className={classes.salaryValue}>
              &#8358;{currencyFormat(Number(data?.desiredCompensation?.lowestAmount ?? 0))}
              <span>&rarr;</span>
              &#8358;{currencyFormat(Number(data?.desiredCompensation?.highestAmount ?? 0))}
            </div>
          </div>

          <div className={classes.resumeRow}>
            {data?.pdfUrl ? (
              <a className={classes.resumeDownload} href={data.pdfUrl} download target="_blank" rel="noopener noreferrer">
                <IconDownload size={14} /> Download
              </a>
            ) : (
              <span className={classes.resumeDownload} style={{ opacity: 0.5, cursor: "not-allowed" }}>
                <IconDownload size={14} /> Download
              </span>
            )}
            <span className={classes.resumeName}>{data?.firstName} Resume</span>
          </div>

          <div className={classes.summaryBox}>
            <div className={classes.summaryLabel}>Summary</div>
            <p className={classes.summaryText}>{data?.summary}</p>

            {!!data?.workMode?.length && (
              <div className={classes.prefList}>
                {data.workMode.map((m) => (
                  <div className={classes.prefItem} key={m}>
                    <IconWorld size={14} /> Open to {m}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Main sections */}
        <div className={classes.main}>
          {!!experiences.length && (
            <div className={classes.card}>
              <div className={classes.cardHeader}>
                <div className={classes.cardIcon}>
                  <IconBriefcase size={18} />
                </div>
                <div className={classes.cardTitle}>Work History</div>
              </div>
              <div className={classes.cardBody}>
                {experiences.map((item, i) => (
                  <div className={classes.expItem} key={i}>
                    <div className={classes.expRail}>
                      {item?.isCurrentJob ? (
                        <span className={`${classes.expDate} ${classes.expDateNow}`}>Current</span>
                      ) : (
                        <span className={classes.expDate}>{item?.endDate ? moment(item.endDate).format("MMM yyyy") : ""}</span>
                      )}
                      <span className={`${classes.expDot} ${!item?.isCurrentJob ? classes.expDotEnd : ""}`} />
                      {i < experiences.length - 1 && <span className={classes.expLine} />}
                    </div>
                    <div>
                      <div className={classes.expTitle}>
                        <IconBriefcase size={16} /> {item?.jobTitle}
                      </div>
                      <div className={classes.expCompany}>{item?.companyName}</div>
                      {item?.industry && (
                        <div className={classes.expIndustry}>
                          <b>Industry</b>
                          <span className={classes.sep}>|</span>
                          {item.industry}
                        </div>
                      )}
                      <span className={classes.expPeriod}>
                        <IconCalendar size={12} /> {moment(item?.startDate).format("MMM yyyy")} —{" "}
                        {item?.isCurrentJob ? "Present" : moment(item?.endDate).format("MMM yyyy")}
                      </span>
                      {item?.jobDescription && <div className={classes.expDesc}>{item.jobDescription}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!!data?.educations?.length && (
            <div className={classes.card}>
              <div className={classes.cardHeader}>
                <div className={classes.cardIcon}>
                  <IconSchool size={18} />
                </div>
                <div className={classes.cardTitle}>Education</div>
              </div>
              <div className={classes.cardBody}>
                {data.educations.map((item, i) => (
                  <div className={classes.eduItem} key={i}>
                    <div className={classes.eduSchoolName}>{item?.schoolName}</div>
                    <div className={classes.eduDegreeRow}>
                      <span className={classes.eduField}>{item?.fieldOfStudy}</span>
                      <span className={classes.eduSep}>&middot;</span>
                      <span className={classes.eduDegree}>{item?.degree}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!!data?.specificPriorities?.length && (
            <div className={classes.card}>
              <div className={classes.cardHeader}>
                <div className={classes.cardIcon}>
                  <IconHeart size={18} />
                </div>
                <div className={classes.cardTitle}>Work Priorities</div>
              </div>
              <div className={classes.cardBody}>
                <div className={classes.priorityList}>
                  {data.specificPriorities.map((p) => (
                    <span className={classes.priorityChip} key={p}>
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {!!(data?.primarySkills?.length || data?.secondarySkills?.length) && (
            <div className={classes.card}>
              <div className={classes.cardHeader}>
                <div className={classes.cardIcon}>
                  <IconBulb size={18} />
                </div>
                <div className={classes.cardTitle}>Skills</div>
              </div>
              <div className={classes.cardBody}>
                <div className={classes.skillList}>
                  {[...(data?.primarySkills || []), ...(data?.secondarySkills || [])].map((s) => (
                    <span className={classes.skillChip} key={s}>
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {!!scores.length && (
            <div className={classes.card}>
              <div className={classes.cardHeader}>
                <div className={classes.cardIcon}>
                  <IconListCheck size={18} />
                </div>
                <div className={classes.cardTitle}>Abilities</div>
              </div>
              <div className={classes.cardBody}>
                <div className={classes.abilitiesGrid}>
                  {scores.map((s) => {
                    const r = ratingFor(s.talent);
                    return (
                      <div className={classes.detailRow} key={s.name}>
                        <span className={classes.detailLabel}>{s.name}</span>
                        <span className={`${classes.detailValue} ${r.cls}`}>{r.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {!!data?.psychometricTestResult?.summary && (
            <div className={classes.card}>
              <div className={classes.cardHeader}>
                <div className={classes.cardIcon}>
                  <IconMessageChatbot size={18} />
                </div>
                <div className={classes.cardTitle}>AI Summary</div>
              </div>
              <div className={classes.cardBody}>
                <p className={classes.aiText}>{data.psychometricTestResult.summary}</p>
              </div>
            </div>
          )}

          {data?.submittedPsychometricTest && !!scores.length && (
            <div className={classes.card}>
              <div className={classes.cardHeader}>
                <div className={classes.cardIcon}>
                  <IconChartBar size={18} />
                </div>
                <div className={classes.cardTitle}>Score Comparison</div>
              </div>
              <div className={classes.cardBody}>
                <div className={classes.scoreLegend}>
                  <span className={classes.scoreLegendItem}>
                    <span className={`${classes.scoreLegendDot} ${classes.scoreLegendDotTalent}`} /> Talent Score
                  </span>
                  <span className={classes.scoreLegendItem}>
                    <span className={`${classes.scoreLegendDot} ${classes.scoreLegendDotAi}`} /> AI Score
                  </span>
                </div>
                {scores.map((s) => (
                  <div className={classes.scoreTraitRow} key={s.name}>
                    <div className={classes.scoreTraitName}>{s.name}</div>
                    <div className={classes.scoreBarsWrap}>
                      <div className={classes.scoreBarCol}>
                        <div className={classes.scoreBarTrack} style={{ direction: "rtl" }}>
                          <div className={classes.scoreBarFillTalent} style={{ width: `${s.talent}%` }} />
                        </div>
                        <span className={`${classes.scoreVal} ${classes.scoreValTalent}`}>{s.talent}</span>
                      </div>
                      <div className={classes.scoreBarCol}>
                        <div className={classes.scoreBarTrack}>
                          <div className={classes.scoreBarFillAi} style={{ width: `${s.ai}%` }} />
                        </div>
                        <span className={`${classes.scoreVal} ${classes.scoreValAi}`}>{s.ai}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewCard;
